import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAnimationConfig, useReducedMotion } from '../lib/animations/context';

interface BrandedLoadingAnimationProps {
  isVisible: boolean;
  type?: 'page' | 'content' | 'form' | 'minimal';
  message?: string;
  progress?: number;
  className?: string;
}

const BrandedLoadingAnimation: React.FC<BrandedLoadingAnimationProps> = ({
  isVisible,
  type = 'page',
  message = 'Loading...',
  progress,
  className = ''
}) => {
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  const overlayVariants = {
    hidden: { 
      opacity: 0,
      backdropFilter: 'blur(0px)'
    },
    visible: { 
      opacity: 1,
      backdropFilter: 'blur(8px)',
      transition: { 
        duration: config.customTimings.normal,
        ease: config.customTimings.defaultEasing
      }
    },
    exit: {
      opacity: 0,
      backdropFilter: 'blur(0px)',
      transition: { 
        duration: config.customTimings.fast
      }
    }
  };

  const contentVariants = {
    hidden: { 
      opacity: 0,
      scale: isReducedMotion ? 1 : 0.8,
      y: isReducedMotion ? 0 : 20
    },
    visible: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { 
        duration: config.customTimings.normal,
        delay: 0.1,
        ease: config.customTimings.defaultEasing
      }
    },
    exit: {
      opacity: 0,
      scale: isReducedMotion ? 1 : 0.8,
      y: isReducedMotion ? 0 : -20,
      transition: { 
        duration: config.customTimings.fast
      }
    }
  };

  const logoVariants = {
    initial: { 
      scale: 1,
      rotate: 0
    },
    animate: { 
      scale: [1, 1.1, 1],
      rotate: isReducedMotion ? 0 : [0, 5, -5, 0],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  const dotsVariants = {
    initial: { opacity: 0.3 },
    animate: { 
      opacity: [0.3, 1, 0.3],
      transition: {
        duration: 1.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };

  const progressVariants = {
    initial: { width: '0%' },
    animate: { 
      width: `${progress || 0}%`,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };

  const renderPageLoader = () => (
    <div className="flex flex-col items-center justify-center space-y-8">
      {/* Branded Logo Animation */}
      <motion.div
        variants={logoVariants}
        initial="initial"
        animate="animate"
        className="relative"
      >
        <div className="w-20 h-20 bg-gradient-to-br from-[#FF5A1F] to-[#FF6B35] rounded-2xl flex items-center justify-center shadow-2xl">
          <motion.div
            animate={{ 
              rotate: isReducedMotion ? 0 : 360 
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear'
            }}
            className="text-white text-2xl font-bold"
          >
            R
          </motion.div>
        </div>
        
        {/* Pulsing ring */}
        {!isReducedMotion && (
          <motion.div
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.5, 0, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
            className="absolute inset-0 border-4 border-[#FF5A1F] rounded-2xl"
          />
        )}
      </motion.div>

      {/* Loading Text */}
      <div className="text-center">
        <motion.h3 
          className="text-xl font-semibold text-gray-800 mb-2"
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        >
          {message}
        </motion.h3>
        
        {/* Animated dots */}
        <div className="flex items-center justify-center space-x-1">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              variants={dotsVariants}
              initial="initial"
              animate="animate"
              transition={{ delay: i * 0.2 }}
              className="w-2 h-2 bg-[#FF5A1F] rounded-full"
            />
          ))}
        </div>
      </div>

      {/* Progress Bar */}
      {typeof progress === 'number' && (
        <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            variants={progressVariants}
            initial="initial"
            animate="animate"
            className="h-full bg-gradient-to-r from-[#FF5A1F] to-[#FF6B35] rounded-full"
          />
        </div>
      )}
    </div>
  );

  const renderContentLoader = () => (
    <div className="flex items-center justify-center space-x-3">
      <motion.div
        animate={{ rotate: isReducedMotion ? 0 : 360 }}
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear'
        }}
        className="w-6 h-6 border-2 border-[#FF5A1F] border-t-transparent rounded-full"
      />
      <span className="text-gray-600 font-medium">{message}</span>
    </div>
  );

  const renderFormLoader = () => (
    <div className="flex items-center justify-center space-x-2">
      <div className="flex space-x-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.5, 1, 0.5]
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              delay: i * 0.2,
              ease: 'easeInOut'
            }}
            className="w-2 h-2 bg-[#FF5A1F] rounded-full"
          />
        ))}
      </div>
      <span className="text-sm text-gray-600">{message}</span>
    </div>
  );

  const renderMinimalLoader = () => (
    <motion.div
      animate={{ rotate: isReducedMotion ? 0 : 360 }}
      transition={{
        duration: 1,
        repeat: Infinity,
        ease: 'linear'
      }}
      className="w-8 h-8 border-2 border-[#FF5A1F] border-t-transparent rounded-full"
    />
  );

  const getLoaderContent = () => {
    switch (type) {
      case 'page':
        return renderPageLoader();
      case 'content':
        return renderContentLoader();
      case 'form':
        return renderFormLoader();
      case 'minimal':
        return renderMinimalLoader();
      default:
        return renderPageLoader();
    }
  };

  const getOverlayClass = () => {
    switch (type) {
      case 'page':
        return 'fixed inset-0 z-50 bg-white/80';
      case 'content':
        return 'absolute inset-0 z-10 bg-white/90';
      case 'form':
        return 'absolute inset-0 z-10 bg-white/95';
      case 'minimal':
        return '';
      default:
        return 'fixed inset-0 z-50 bg-white/80';
    }
  };

  if (type === 'minimal') {
    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={className}
          >
            {getLoaderContent()}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          className={`${getOverlayClass()} flex items-center justify-center ${className}`}
        >
          <motion.div
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {getLoaderContent()}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BrandedLoadingAnimation;